import { useEffect } from 'react';
import { diag, summarizeContainer } from '@/lib/diag';

// Structural, dependency-free shapes — importing the concrete ContainerAccess /
// UserProfile from `@/types/session` here would create a common↔types module
// cycle (import-direction / ADR 024). We only describe the fields we read; the
// real types (a superset) are structurally assignable at the call site.
interface GateContainer {
  id: string;
  isOwner?: boolean;
  permission?: string;
  role?: string;
  type?: string;
  onboardingCompleted?: boolean;
}
interface GateProfile {
  trialStartedAt?: number | null;
  isPaidAccount?: boolean | null;
}
interface GateStateDiagInput {
  status: string;
  profile: GateProfile | null;
  containersLoaded: boolean;
  containerReady: boolean;
  appEntryReady: boolean;
  enteringApp: boolean;
  confirmedZeroWorkspace: boolean;
  containers: GateContainer[] | undefined;
  pendingInvites: unknown[] | undefined;
  selectedContainer: GateContainer | null;
}

/**
 * #5105 loop diagnostics: snapshot the gate-decision inputs whenever they change,
 * so the reload-surviving buffer (lib/diag) shows which screen the member-only
 * session cycles through (the team-member-only login loop). Inert in normal use
 * (one entry per state transition); never logs tokens/PII.
 *
 * Extracted out of App.tsx to keep the composition shell under the file-size
 * ceiling and to keep `common/` free of `features/` imports (import-direction).
 */
export function useGateStateDiag(input: GateStateDiagInput): void {
  const {
    status,
    profile,
    containersLoaded,
    containerReady,
    appEntryReady,
    enteringApp,
    confirmedZeroWorkspace,
    containers,
    pendingInvites,
    selectedContainer,
  } = input;

  useEffect(() => {
    diag('gate', 'state', {
      status,
      hasProfile: Boolean(profile),
      containersLoaded,
      containerReady,
      appEntryReady,
      enteringApp,
      confirmedZeroWorkspace,
      containersCount: containers?.length ?? 0,
      pendingInvites: pendingInvites?.length ?? 0,
      hasTeamMembership: (containers ?? []).some((c) => c.isOwner === false),
      selected: selectedContainer ? summarizeContainer(selectedContainer) : null,
      trialStartedAt: profile?.trialStartedAt ?? null,
      isPaidAccount: profile?.isPaidAccount ?? null,
    });
  }, [
    status,
    profile,
    containersLoaded,
    containerReady,
    appEntryReady,
    enteringApp,
    confirmedZeroWorkspace,
    containers,
    pendingInvites,
    selectedContainer,
  ]);
}
